// Pure helpers for the upload-link page (kept apart so they are unit-tested).

// The folder-relative path of a picked file, or undefined for a single file.
// A folder <input webkitdirectory> sets webkitRelativePath ("Shoot/a.jpg");
// react-dropzone sets path for dropped folders ("/Shoot/a.jpg"), and for a
// dropped single file just its name.
export function folderPath(file: File): string | undefined {
  const raw = (file as any).webkitRelativePath || (file as any).path || '';
  const path = String(raw).replace(/^\.?\/+/, '');
  return path.includes('/') ? path : undefined;
}

// The same rule as the backend: "image/*" matches the major type, anything
// else must match exactly. No patterns = any type.
export function typeAllowed(contentType: string, patterns?: string[] | null): boolean {
  if (!patterns || patterns.length === 0) return true;
  const type = (contentType || '').split(';')[0].trim().toLowerCase();
  if (!type.includes('/')) return false;
  const major = type.split('/')[0];
  return patterns.some((p) => p === type || p === `${major}/*`);
}

const TYPE_NAMES: Record<string, string> = {
  'image/*': 'images',
  'video/*': 'videos',
  'audio/*': 'audio files',
  'text/*': 'text files',
  'application/pdf': 'PDFs',
};

// Content-type patterns as people say them: "image/*" -> "images". Anything
// we have no name for is shown as written.
export function typeNames(patterns?: string[] | null): string[] {
  return (patterns ?? []).map((p) => TYPE_NAMES[p] ?? p);
}

// For the file picker's accept attribute.
export function acceptAttribute(patterns?: string[] | null): string | undefined {
  return patterns && patterns.length ? patterns.join(',') : undefined;
}

const ASSISTANTS: Record<string, string> = {
  claude: 'Claude',
  'claude-ai': 'Claude',
  'anthropic-claudeai': 'Claude',
  'claude-code': 'Claude Code',
  'claude-desktop': 'Claude',
  chatgpt: 'ChatGPT',
  openai: 'ChatGPT',
  codex: 'Codex',
  cursor: 'Cursor',
  vscode: 'VS Code',
  'visual-studio-code': 'VS Code',
  windsurf: 'Windsurf',
};

// client names that say nothing about which assistant it is
const GENERIC = new Set(['mcp', 'mcp-client', 'client', 'unknown']);

// The assistant's name for the page's "Your AI assistant (name)":
// 1. app_name, the connected app as the user knows it from Connected apps
//    (their own label for it, or the app that asked to connect, e.g. "Claude");
// 2. a known MCP client name, as people say it;
// 3. any other client name, tidied ("acme-agent" -> "Acme Agent").
// Undefined when there is nothing better than "Your AI assistant".
export function assistantName(appName?: string | null, createdVia?: string | null): string | undefined {
  if (appName && appName.trim()) return appName.trim();
  if (!createdVia) return undefined;
  const key = createdVia.toLowerCase();
  const known = ASSISTANTS[key] ?? Object.entries(ASSISTANTS).find(([k]) => key.startsWith(`${k}-`))?.[1];
  if (known) return known;
  if (GENERIC.has(key)) return undefined;
  return key
    .split(/[-_.]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

// How many more files the link takes, counting files already queued here.
export function remainingSlots(maxFiles: number, uploadedCount: number, pendingHere: number): number {
  return Math.max(0, maxFiles - uploadedCount - pendingHere);
}
